'use client'
import { memo, useMemo } from 'react'
import ErrorBoundary from '@error-boundary'
import clsx from 'clsx'
import dynamic from 'next/dynamic'
import Link from 'next/link'

import type { CartItemProps } from '@typings'
import {
  RaButton,
  RaImage,
  RaPaper,
  RaTypography,
} from '@components/core/index.core'
import { ICONS } from '@constants/icons.constants'
import { useCart } from '@contexts/cart.context'
import { useSettings } from '@contexts/settings.context'
import {
  Loop,
  stripFrontendUrl,
  stripWpUrl,
  translate,
} from '@utilities/helpers/core.helpers'

import styles from './ra-shopping-cart.module.scss'

const RaShareTravelplan = dynamic(() =>
  import('@components/features/RaShareTravelplan/RaShareTravelplan').then(
    ({ RaShareTravelplan }) => RaShareTravelplan
  )
)

interface RaShoppingCartProps {
  showCartDetails: boolean
  openShoppingCart: () => void
  closeShoppingCart: () => void
}

export const RaShoppingCart = memo(
  ({
    showCartDetails,
    openShoppingCart,
    closeShoppingCart,
  }: RaShoppingCartProps) => {
    const cart = useCart()
    const { themeSettings } = useSettings()

    const travelplanLabel = translate('REQUEST_TRAVELPLAN_NO_OBLIGATIONS')

    const cartItems = useMemo(() => {
      if (!cart?.cartItems || cart.cartItemsCount === 0)
        return (
          <RaTypography
            weight="semibold"
            size="l"
            align="center">
            {translate('BASKET_IS_EMPTY')}
          </RaTypography>
        )

      const output = Loop(cart.cartItems, (item: CartItemProps, i: number) => {
        return (
          <li
            key={`${item.cartId}-${i}`}
            className={styles['ra-shopping-cart__item']}>
            <Link
              prefetch={false}
              href={stripFrontendUrl(item.link?.url)}
              title={item.link?.title}
              target="_blank">
              <RaImage
                image={item.image}
                width={64}
                height={48}
              />
              <RaTypography
                weight="semibold"
                size="l"
                component="span"
                customClasses={styles['ra-shopping-cart__item__title']}>
                {item?.title}
              </RaTypography>
              <RaButton
                onClick={e => {
                  e.preventDefault()
                  cart.removeFromCart(item)
                }}
                variant="icon"
                icon={ICONS.CLOSE_ICON}
                textColor="dark"
              />
            </Link>
          </li>
        )
      })

      return <ul className={styles['ra-shopping-cart__list']}>{output}</ul>
    }, [cart?.cartItems])

    const dropdownClasses = useMemo(() => {
      return clsx(styles['ra-shopping-cart'], showCartDetails && styles['open'])
    }, [showCartDetails])

    return (
      <ErrorBoundary>
        <div
          className={dropdownClasses}
          onMouseEnter={openShoppingCart}
          onMouseLeave={closeShoppingCart}
          style={{ opacity: 0, display: 'none' }}>
          <RaPaper tornEdge>
            {cartItems}
            <div className={styles['ra-shopping-cart__buttons']}>
              <RaButton
                fullWidth
                link={{
                  title: themeSettings.travelplan_page.title,
                  url: stripWpUrl(themeSettings.travelplan_page.url),
                }}
                label={travelplanLabel}
              />
              <RaShareTravelplan />
            </div>
          </RaPaper>
        </div>
      </ErrorBoundary>
    )
  }
)
